import { NBN_PERSISTENT_EXPIRE_SPAN, NBN_SESSION_KEYS } from '@belong/constants';
import { toSingleLineAddress } from '@belong/domain-address-capture';
import { isBrowser } from '@belong/env';
import { logger } from '@belong/logging/logger';
import { clearKeyFromSessionStorage, getValuesFromSessionStorage } from '@belong/session-storage';
import {
  INbnOrder,
  INbnOrderPrice,
  IOrderSummaryPlanRefresh,
  ISQFlow,
  MODEM_SELECTION,
  NBN_PAYMENT_OPTION,
  NDC_CHARGE,
  SQStep
} from '@belong/types';
import { makeFullAddressFromStrategicAddressEntity } from '../address';

export const AUTHORISATION_FEE = 1;

const MONTHS_PER_YEAR = 12;

export interface IProductInfo {
  option: NBN_PAYMENT_OPTION;
  productCode: string;
  price: number;
}

export const isOrderDataValid = (order: Partial<INbnOrder>): boolean => {
  const values = {
    orderId: order.orderId,
    status: order.status,
    serviceAddress: order.serviceAddress,
    serviceQualification: order.serviceQualification,
    planProductCode: order.planProductCode,
    price: order.price
  };
  return Object.entries(values).every(([key, value]) => {
    if (value === undefined) {
      logger.debug(`isOrderDataValid - key '${key}' in order is undefined`);
      return false;
    }

    return true;
  });
};

export const isSqFlowValid = (): boolean => {
  // check if sqFlow in sessionStorage is valid - if not, we should start a new flow instead of trying to use the invalid session data
  const sessionSQFlow = getValuesFromSessionStorage<ISQFlow>(NBN_SESSION_KEYS.SQ_FLOW);

  /* 
    adding a guard in here so as not to set sqFlow if the step is 'serviceability-details'
    and there is no selectedAddress in the NBN_SESSION_KEYS.SQ_FLOW. There is an edge case
    where this can happen if the user refreshes the page while on the serviceability details step
    */

  if (sessionSQFlow?.step === SQStep.SERVICEABILITY_DETAILS && !sessionSQFlow?.selectedAddress) {
    return false;
  }

  return true;
};

export const loadSqFlowFromSession = (): ISQFlow | null => {
  if (!isBrowser() || !isSqFlowValid()) {
    return null;
  }

  const sessionSQFlow = getValuesFromSessionStorage<ISQFlow>(NBN_SESSION_KEYS.SQ_FLOW);

  if (!sessionSQFlow?.sqLastDoneAt) {
    return sessionSQFlow;
  }

  const isExpired = Date.now() - sessionSQFlow.sqLastDoneAt > NBN_PERSISTENT_EXPIRE_SPAN;

  return isExpired ? null : sessionSQFlow;
};

export const resetStrategicSQFlow = (): void => {
  clearKeyFromSessionStorage(NBN_SESSION_KEYS.SQ_FLOW);
};

/**
 * converts the order data from the API (or the store/session which will have the exact same structure)
 * into an object that is compatible with the current order summary implementation.
 *
 * TODO: order summary is planned to be refactored so that no intermediary conversions are required
 */
export const constructOrderSummaryData = (
  order?: INbnOrder,
  modemSelection?: MODEM_SELECTION
): IOrderSummaryPlanRefresh | undefined => {
  if (!order) {
    return;
  }

  if (!isOrderDataValid(order)) {
    return;
  }

  const {
    serviceAddress,
    planName,
    modemPaymentOption,
    ndcPaymentOption,
    promotion,
    promotionCode,
    serviceQualification: { newDevelopmentsCharge },
    price: {
      planCost,
      modemRecurringCost,
      modemUpfrontCost,
      minimumTerminationCost,
      ndcRecurringCost,
      ndcUpfrontCost,
      totalRecurringCost
    }
  } = order;

  const address =
    'locality' in serviceAddress
      ? toSingleLineAddress(serviceAddress)
      : makeFullAddressFromStrategicAddressEntity(serviceAddress);

  return {
    authFee: AUTHORISATION_FEE,
    /** Address */
    address,
    /** Plan */
    planName,
    /** Modem */
    modemCostMonthly: modemRecurringCost ?? 0,
    modemCostUpfront: modemUpfrontCost ?? 0,
    modemPaymentOption,
    modemSelection,
    isFreeModemOfferActive: false, // TODO: need to be updated when implicit promotion in place
    /** NDC */
    ndcCostMonthly: ndcRecurringCost ?? 0,
    ndcCostUpfront: ndcUpfrontCost ?? 0,
    ndcPayUpfront: ndcPaymentOption === NBN_PAYMENT_OPTION.UPFRONT,
    isNdcRequired: newDevelopmentsCharge === NDC_CHARGE.YES && !!ndcPaymentOption,
    /** Promotions */
    creditOccurrence: promotion?.occurrence ?? 0,
    creditAmount: promotion?.value ?? 0,
    promotionCode: promotionCode ?? '',
    /** Costs */
    productCostMonthlyTotal: planCost,
    minimumCost: minimumTerminationCost,
    monthlyCostTotal: totalRecurringCost
  };
};

const updateTotalCostUponModemOptionChange = (
  order: INbnOrder,
  modemProduct?: IProductInfo
): Pick<INbnOrderPrice, 'totalRecurringCost' | 'totalUpfrontCost' | 'minimumTerminationCost'> => {
  const {
    totalRecurringCost,
    modemRecurringCost = 0,
    totalUpfrontCost,
    modemUpfrontCost = 0,
    minimumTerminationCost
  } = order.price;
  // There are 3 options for modemProduct, monthly, upfront and byo
  const { option, price } = modemProduct || {};
  let totalCost = {
    totalRecurringCost,
    totalUpfrontCost,
    minimumTerminationCost
  };
  if (option !== order.modemPaymentOption) {
    // BE is BYO, yet FE has other options
    if (!option || !price) {
      totalCost = {
        totalRecurringCost:
          totalRecurringCost -
          (order.modemPaymentOption === NBN_PAYMENT_OPTION.MONTH_BY_MONTH ? modemRecurringCost : 0),
        totalUpfrontCost:
          totalUpfrontCost - (order.modemPaymentOption === NBN_PAYMENT_OPTION.UPFRONT ? modemUpfrontCost : 0),
        minimumTerminationCost: order.modemPaymentOption
          ? minimumTerminationCost -
            (order.modemPaymentOption === NBN_PAYMENT_OPTION.MONTH_BY_MONTH
              ? (modemRecurringCost || 0) * MONTHS_PER_YEAR
              : modemUpfrontCost || 0)
          : minimumTerminationCost
      };
    } else if (option === NBN_PAYMENT_OPTION.MONTH_BY_MONTH) {
      totalCost = {
        totalRecurringCost: totalRecurringCost + price,
        totalUpfrontCost: totalUpfrontCost - (modemUpfrontCost || 0),
        minimumTerminationCost: order.modemPaymentOption
          ? minimumTerminationCost
          : minimumTerminationCost + price * MONTHS_PER_YEAR
      };
    } else if (option === NBN_PAYMENT_OPTION.UPFRONT) {
      totalCost = {
        totalRecurringCost: totalRecurringCost - modemRecurringCost,
        totalUpfrontCost: totalUpfrontCost + price,
        minimumTerminationCost: order.modemPaymentOption ? minimumTerminationCost : minimumTerminationCost + price
      };
    }
  }

  return totalCost;
};

const updateTotalCostUponNdcOptionChange = (
  order: INbnOrder,
  ndcProduct: IProductInfo
): Pick<INbnOrderPrice, 'totalRecurringCost' | 'totalUpfrontCost' | 'minimumTerminationCost'> => {
  const { totalRecurringCost, ndcRecurringCost, totalUpfrontCost, ndcUpfrontCost, minimumTerminationCost } =
    order.price;
  const { option, price } = ndcProduct;
  // For NDC there are only 2 options, monthly and upfront, there is no `not_applicable`, so here just consider these 2 options
  let totalCost = {
    totalRecurringCost,
    totalUpfrontCost,
    minimumTerminationCost
  };
  if (option !== order.ndcPaymentOption) {
    if (option === NBN_PAYMENT_OPTION.MONTH_BY_MONTH) {
      totalCost = {
        totalRecurringCost: totalRecurringCost + price,
        totalUpfrontCost: totalUpfrontCost - (ndcUpfrontCost || 0),
        minimumTerminationCost: order.ndcPaymentOption
          ? minimumTerminationCost
          : minimumTerminationCost + price * MONTHS_PER_YEAR
      };
    }

    if (option === NBN_PAYMENT_OPTION.UPFRONT) {
      totalCost = {
        totalRecurringCost: totalRecurringCost - (ndcRecurringCost || 0),
        totalUpfrontCost: totalUpfrontCost + price,
        minimumTerminationCost: order.ndcPaymentOption ? minimumTerminationCost : minimumTerminationCost + price
      };
    }
  }

  return totalCost;
};

export const modifyOrderForNdc = (order: INbnOrder, ndcProduct: IProductInfo): INbnOrder => {
  const { productCode: ndcProductCode, option: ndcPaymentOption, price } = ndcProduct;

  const isUpfront = ndcPaymentOption === NBN_PAYMENT_OPTION.UPFRONT;
  let totalCost: ReturnType<typeof updateTotalCostUponNdcOptionChange> | undefined;

  /** Monthly NDC cost */
  const ndcRecurringCost = isUpfront ? 0 : price;
  /** Upfront NDC cost */
  const ndcUpfrontCost = isUpfront ? price : 0;

  if (order.price) {
    totalCost = updateTotalCostUponNdcOptionChange(order, ndcProduct);
  }

  return {
    ...order,
    ndcPaymentOption,
    ndcProductCode,
    price: {
      ...order.price,
      ...totalCost,
      ndcRecurringCost,
      ndcUpfrontCost
    }
  };
};

// when modemProduct is undefined, it's BYO
export const modifyOrderForModem = (order: INbnOrder, modemProduct?: IProductInfo): INbnOrder => {
  const { productCode: modemProductCode, option: modemPaymentOption, price } = modemProduct || {};

  const isUpfront = modemPaymentOption === NBN_PAYMENT_OPTION.UPFRONT;
  let totalCost: ReturnType<typeof updateTotalCostUponModemOptionChange> | undefined;

  /** Monthly modem cost */
  const modemRecurringCost = isUpfront ? 0 : price || 0;
  /** Upfront modem cost */
  const modemUpfrontCost = isUpfront ? price : 0;

  if (order.price) {
    totalCost = updateTotalCostUponModemOptionChange(order, modemProduct);
  }

  const {
    modemRecurringCost: _modemRecurringCost,
    modemUpfrontCost: _modemUpfrontCost,
    ...basePrice
  } = order.price || {};

  return {
    ...order,
    modemPaymentOption,
    modemProductCode,
    price: {
      ...basePrice,
      ...totalCost,
      ...(modemProduct ? { modemRecurringCost, modemUpfrontCost } : {})
    }
  };
};
